import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Switch,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Textarea,
  Typography,
} from '@mui/joy';
import { useTheme } from '@mui/joy/styles';

import {
  ApiError,
  createCongregationBook,
  createCongregationCourse,
  createCongregationSpiritualMaster,
  fetchCongregationMasters,
  updateCongregationBook,
  updateCongregationCourse,
  updateCongregationSpiritualMaster,
} from '../../api';
import { normalizeMastersPayload } from '../../congregation/utils';
import type {
  CongregationBook,
  CongregationBookInput,
  CongregationCourse,
  CongregationCourseInput,
  CongregationMastersPayload,
  CongregationSpiritualMaster,
  CongregationSpiritualMasterInput,
} from '../../types/congregation';
import { MasterSection, type ColumnDefinition } from '../../congregation/components/MasterSection';
import { useSnackbarStore } from '../../store/useSnackbarStore';

interface DialogState<T> {
  open: boolean;
  record: T | null;
}

const emptyDialogState = { open: false, record: null } as const;

type MasterTab =
  | 'courses'
  | 'books'
  | 'spiritualMasters';

const COURSE_NAME_MIN_LENGTH = 2;
const COURSE_NAME_MAX_LENGTH = 120;
const COURSE_DESCRIPTION_MAX_LENGTH = 500;
const BOOK_TITLE_MIN_LENGTH = 2;
const BOOK_TITLE_MAX_LENGTH = 160;
const BOOK_AUTHOR_MIN_LENGTH = 2;
const BOOK_AUTHOR_MAX_LENGTH = 160;
const BOOK_DESCRIPTION_MAX_LENGTH = 600;
const SPIRITUAL_MASTER_NAME_MIN_LENGTH = 2;
const SPIRITUAL_MASTER_NAME_MAX_LENGTH = 160;

const emptySearchState: Record<MasterTab, string> = {
  courses: '',
  books: '',
  spiritualMasters: '',
};

export default function CongregationMastersPage() {
  const { showSnackbar } = useSnackbarStore();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const heroBackground = isDark
    ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(10, 15, 26, 0.98) 100%)'
    : 'radial-gradient(120% 140% at 8% 0%, rgba(255, 218, 170, 0.2) 0%, rgba(255, 218, 170, 0) 55%), radial-gradient(120% 140% at 95% 5%, rgba(165, 197, 255, 0.2) 0%, rgba(165, 197, 255, 0) 60%), linear-gradient(180deg, rgba(255, 252, 245, 1) 0%, rgba(248, 250, 255, 1) 45%, rgba(244, 246, 252, 1) 100%)';
  const heroOverlay = isDark
    ? 'radial-gradient(120% 120% at 15% -10%, rgba(56, 189, 248, 0.14), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(251, 191, 36, 0.12), transparent 55%)'
    : 'radial-gradient(120% 120% at 15% -10%, rgba(255, 220, 160, 0.45), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(129, 158, 255, 0.28), transparent 55%)';
  const heroTitle = isDark ? 'rgba(248,250,252,0.94)' : 'text.primary';
  const heroBody = isDark ? 'rgba(226,232,240,0.8)' : 'text.secondary';
  const panelWarm = isDark
    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95))'
    : 'linear-gradient(135deg, rgba(255, 237, 213, 0.9), rgba(255, 247, 231, 0.9))';
  const panelCool = isDark
    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.95))'
    : 'linear-gradient(135deg, rgba(224, 243, 255, 0.8), rgba(241, 249, 255, 0.95))';
  const orbColor = isDark ? 'rgba(56, 189, 248, 0.18)' : 'rgba(255, 214, 153, 0.35)';
  const [loading, setLoading] = React.useState(true);
  const [masters, setMasters] = React.useState<CongregationMastersPayload | null>(null);
  const [activeTab, setActiveTab] = React.useState<MasterTab>('courses');
  const [searchQueries, setSearchQueries] = React.useState<Record<MasterTab, string>>(emptySearchState);

  const [courseDialog, setCourseDialog] = React.useState<DialogState<CongregationCourse>>(emptyDialogState);
  const [bookDialog, setBookDialog] = React.useState<DialogState<CongregationBook>>(emptyDialogState);
  const [spiritualMasterDialog, setSpiritualMasterDialog] = React.useState<DialogState<CongregationSpiritualMaster>>(emptyDialogState);

  const [saving, setSaving] = React.useState(false);
  const mastersSummary = React.useMemo(() => {
    return {
      courses: masters?.courses?.length ?? 0,
      books: masters?.books?.length ?? 0,
      spiritualMasters: masters?.spiritualMasters?.length ?? 0,
      activeMasters: (masters?.spiritualMasters ?? []).filter((master) => master.isActive).length,
    };
  }, [masters]);

  const updateSearchQuery = React.useCallback((tab: MasterTab, value: string) => {
    setSearchQueries((prev) => ({ ...prev, [tab]: value }));
  }, []);

  const highlightConflict = React.useCallback(
    (tab: MasterTab, query: string, entityLabel: string) => {
      const trimmed = query.trim();
      setActiveTab(tab);
      if (trimmed) {
        updateSearchQuery(tab, trimmed);
        showSnackbar(`${entityLabel} already exists. Highlighted the existing record.`, 'warning');
      } else {
        showSnackbar(`${entityLabel} already exists.`, 'warning');
      }
    },
    [showSnackbar, updateSearchQuery],
  );

  const loadMasters = React.useCallback(async () => {
    try {
      setLoading(true);
      const payload = await fetchCongregationMasters();
      setMasters(normalizeMastersPayload(payload));
    } catch (error) {
      console.error('Failed to load congregation masters', error);
      showSnackbar('Unable to load congregation master data.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  React.useEffect(() => {
    loadMasters();
  }, [loadMasters]);

  const handleCourseSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const name = String(formData.get('name') ?? '').trim();
    const orderIndexValue = String(formData.get('orderIndex') ?? '').trim();
    const descriptionValue = String(formData.get('description') ?? '').trim();
    if (name.length < COURSE_NAME_MIN_LENGTH) {
      showSnackbar(`Course name must be at least ${COURSE_NAME_MIN_LENGTH} characters.`, 'warning');
      return;
    }
    if (name.length > COURSE_NAME_MAX_LENGTH) {
      showSnackbar(`Course name cannot exceed ${COURSE_NAME_MAX_LENGTH} characters.`, 'warning');
      return;
    }
    if (descriptionValue && descriptionValue.length > COURSE_DESCRIPTION_MAX_LENGTH) {
      showSnackbar(
        `Course description cannot exceed ${COURSE_DESCRIPTION_MAX_LENGTH} characters.`,
        'warning',
      );
      return;
    }
    const orderIndex = orderIndexValue ? parseInt(orderIndexValue, 10) : null;
    if (orderIndex !== null && (isNaN(orderIndex) || orderIndex < 1)) {
      showSnackbar('Order must be a positive number.', 'warning');
      return;
    }
    const payload: CongregationCourseInput = {
      name,
      orderIndex,
      description: descriptionValue || null,
    };
    try {
      setSaving(true);
      if (courseDialog.record) {
        await updateCongregationCourse(courseDialog.record.id, payload);
      } else {
        await createCongregationCourse(payload);
      }
      showSnackbar('Course saved successfully.', 'success');
      setCourseDialog(emptyDialogState);
      await loadMasters();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        highlightConflict('courses', name, 'A course with this name');
      } else {
        console.error('Failed to save course', error);
        showSnackbar('Unable to save course. Please try again.', 'danger');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBookSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const title = String(formData.get('title') ?? '').trim();
    const authorValue = String(formData.get('author') ?? '').trim();
    const descriptionValue = String(formData.get('description') ?? '').trim();
    const orderValue = String(formData.get('orderIndex') ?? '').trim();
    if (title.length < BOOK_TITLE_MIN_LENGTH) {
      showSnackbar(`Book title must be at least ${BOOK_TITLE_MIN_LENGTH} characters.`, 'warning');
      return;
    }
    if (title.length > BOOK_TITLE_MAX_LENGTH) {
      showSnackbar(`Book title cannot exceed ${BOOK_TITLE_MAX_LENGTH} characters.`, 'warning');
      return;
    }
    if (authorValue) {
      if (authorValue.length < BOOK_AUTHOR_MIN_LENGTH) {
        showSnackbar(
          `Author name must be at least ${BOOK_AUTHOR_MIN_LENGTH} characters when provided.`,
          'warning',
        );
        return;
      }
      if (authorValue.length > BOOK_AUTHOR_MAX_LENGTH) {
        showSnackbar(`Author name cannot exceed ${BOOK_AUTHOR_MAX_LENGTH} characters.`, 'warning');
        return;
      }
    }
    if (descriptionValue && descriptionValue.length > BOOK_DESCRIPTION_MAX_LENGTH) {
      showSnackbar(
        `Book description cannot exceed ${BOOK_DESCRIPTION_MAX_LENGTH} characters.`,
        'warning',
      );
      return;
    }
    let orderIndex: number | null = null;
    if (orderValue.length > 0) {
      const parsedOrder = Number(orderValue);
      if (!Number.isInteger(parsedOrder) || parsedOrder < 0) {
        showSnackbar('Display order must be a non-negative integer.', 'warning');
        return;
      }
      orderIndex = parsedOrder;
    }
    const payload: CongregationBookInput = {
      title,
      author: authorValue || null,
      description: descriptionValue || null,
      orderIndex,
      isPrimary: formData.get('isPrimary') === 'on',
    };
    try {
      setSaving(true);
      if (bookDialog.record) {
        await updateCongregationBook(bookDialog.record.id, payload);
      } else {
        await createCongregationBook(payload);
      }
      showSnackbar('Book saved successfully.', 'success');
      setBookDialog(emptyDialogState);
      await loadMasters();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        highlightConflict('books', title, 'A book with this title');
      } else {
        console.error('Failed to save book', error);
        showSnackbar('Unable to save book. Please try again.', 'danger');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSpiritualMasterSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const name = String(formData.get('name') ?? '').trim();
    if (name.length < SPIRITUAL_MASTER_NAME_MIN_LENGTH) {
      showSnackbar(
        `Spiritual master name must be at least ${SPIRITUAL_MASTER_NAME_MIN_LENGTH} characters.`,
        'warning',
      );
      return;
    }
    if (name.length > SPIRITUAL_MASTER_NAME_MAX_LENGTH) {
      showSnackbar(
        `Spiritual master name cannot exceed ${SPIRITUAL_MASTER_NAME_MAX_LENGTH} characters.`,
        'warning',
      );
      return;
    }
    const payload: CongregationSpiritualMasterInput = {
      name,
      isActive: formData.get('isActive') === 'on',
    };
    try {
      setSaving(true);
      if (spiritualMasterDialog.record) {
        await updateCongregationSpiritualMaster(spiritualMasterDialog.record.id, payload);
      } else {
        await createCongregationSpiritualMaster(payload);
      }
      showSnackbar('Spiritual master saved successfully.', 'success');
      setSpiritualMasterDialog(emptyDialogState);
      await loadMasters();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        highlightConflict('spiritualMasters', name, 'A spiritual master with this name');
      } else {
        console.error('Failed to save spiritual master', error);
        showSnackbar('Unable to save spiritual master. Please try again.', 'danger');
      }
    } finally {
      setSaving(false);
    }
  };


  const sortedCourses = React.useMemo(
    () =>
      (masters?.courses ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [masters],
  );

  const sortedBooks = React.useMemo(
    () =>
      (masters?.books ?? [])
        .slice()
        .sort((a, b) => {
          const orderA = a.orderIndex ?? Number.MAX_SAFE_INTEGER;
          const orderB = b.orderIndex ?? Number.MAX_SAFE_INTEGER;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          return a.title.localeCompare(b.title);
        }),
    [masters],
  );

  const sortedSpiritualMasters = React.useMemo(
    () =>
      (masters?.spiritualMasters ?? [])
        .slice()
        .sort((a, b) => {
          if (a.isActive !== b.isActive) {
            return a.isActive ? -1 : 1;
          }
          return a.name.localeCompare(b.name);
        }),
    [masters],
  );


  const courseColumns: ColumnDefinition<CongregationCourse>[] = [
    {
      header: 'Course',
      render: (course) => (
        <Stack spacing={0.5}>
          <Typography level="body-sm" fontWeight={600}>
            {course.name}
          </Typography>
          {course.description ? (
            <Typography level="body-xs" color="neutral">
              {course.description}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      header: 'Order',
      align: 'center',
      width: 100,
      render: (course) => course.orderIndex ?? '—',
    },
    // Removed eligibility requirement display since eligibility tracking is deprecated
  ];

  const bookColumns: ColumnDefinition<CongregationBook>[] = [
    {
      header: 'Title',
      render: (book) => (
        <Stack spacing={0.5}>
          <Typography level="body-sm" fontWeight={600}>
            {book.title}
          </Typography>
          {book.author ? (
            <Typography level="body-xs" color="neutral">
              {book.author}
            </Typography>
          ) : null}
          {book.description ? (
            <Typography level="body-xs" color="neutral">
              {book.description}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      header: 'Primary',
      align: 'center',
      width: 140,
      render: (book) => (
        <Chip
          size="sm"
          color={book.isPrimary ? 'success' : 'neutral'}
          variant={book.isPrimary ? 'solid' : 'soft'}
        >
          {book.isPrimary ? 'Primary' : 'Supplemental'}
        </Chip>
      ),
    },
    {
      header: 'Order',
      render: (book) => (book.orderIndex ?? '—'),
      width: 120,
      align: 'right',
    },
  ];

  const spiritualMasterColumns: ColumnDefinition<CongregationSpiritualMaster>[] = [
    {
      header: 'Name',
      render: (master) => (
        <Stack spacing={0.5}>
          <Typography level="body-sm" fontWeight={600}>
            {master.name}
          </Typography>
          <Typography level="body-xs" color="neutral">
            {master.centerName ?? 'No center assigned'}
          </Typography>
        </Stack>
      ),
    },
    {
      header: 'Status',
      width: 140,
      align: 'center',
      render: (master) => (
        <Chip
          size="sm"
          color={master.isActive ? 'success' : 'neutral'}
          variant={master.isActive ? 'solid' : 'soft'}
        >
          {master.isActive ? 'Active' : 'Inactive'}
        </Chip>
      ),
    },
  ];


  return (
    <Box>
      <Stack spacing={{ xs: 2.5, md: 3.5 }}>
        <Card
          variant="outlined"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 'xl',
            borderColor: 'divider',
            boxShadow: 'lg',
            background: heroBackground,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: heroOverlay,
              opacity: 0.9,
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              width: 180,
              height: 180,
              borderRadius: '50%',
              right: { xs: -60, md: -20 },
              top: { xs: 30, md: 10 },
              background: orbColor,
              filter: 'blur(6px)',
              animation: 'masters-float 8s ease-in-out infinite',
              '@keyframes masters-float': {
                '0%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-12px)' },
                '100%': { transform: 'translateY(0px)' },
              },
              pointerEvents: 'none',
            }}
          />
          <CardContent sx={{ position: 'relative' }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
                <Stack spacing={0.75}>
                  <Typography level="h2" sx={{ fontWeight: 800, color: heroTitle }}>
                    Congregation master data
                  </Typography>
                  <Typography level="body-sm" sx={{ color: heroBody, maxWidth: 760 }}>
                    Maintain the shared reference lists powering congregation onboarding and reporting.
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip variant="soft" color="primary">
                    {mastersSummary.courses} courses
                  </Chip>
                  <Chip variant="soft" color="success">
                    {mastersSummary.activeMasters} active masters
                  </Chip>
                </Stack>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: 'lg',
                    background: panelWarm,
                    boxShadow: 'sm',
                  }}
                >
                  <CardContent>
                    <Typography level="title-sm" sx={{ color: 'text.secondary' }}>
                      Library catalogue
                    </Typography>
                    <Typography level="title-lg" sx={{ mt: 0.5 }}>
                      {mastersSummary.books} books curated
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Keep reading lists aligned with programs.
                    </Typography>
                  </CardContent>
                </Card>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: 'lg',
                    background: panelCool,
                    boxShadow: 'sm',
                  }}
                >
                  <CardContent>
                    <Typography level="title-sm" sx={{ color: 'text.secondary' }}>
                      Mentorship pool
                    </Typography>
                    <Typography level="title-lg" sx={{ mt: 0.5 }}>
                      {mastersSummary.spiritualMasters} masters
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Active mentors ready for applications.
                    </Typography>
                  </CardContent>
                </Card>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {loading || !masters ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size="md" aria-label="Loading congregation masters" />
          </Box>
        ) : (
          <Tabs
            value={activeTab}
            onChange={(_, value) => {
              if (value !== null) {
                setActiveTab(value as MasterTab);
              }
            }}
            sx={{
              borderRadius: 'lg',
              backgroundColor: 'background.surface',
              boxShadow: 'sm',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <TabList
              disableUnderline
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                px: 1,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'transparent',
              }}
            >
              <Tab value="courses" sx={{ borderRadius: 'full' }}>
                Courses ({sortedCourses.length})
              </Tab>
              <Tab value="books" sx={{ borderRadius: 'full' }}>
                Books ({sortedBooks.length})
              </Tab>
              <Tab value="spiritualMasters" sx={{ borderRadius: 'full' }}>
                Spiritual masters ({sortedSpiritualMasters.length})
              </Tab>
            </TabList>
            <TabPanel value="courses" sx={{ p: 0, pt: 3 }}>
              <MasterSection
                title="Courses"
                description="Courses referenced for devotional tracking."
                items={sortedCourses}
                columns={courseColumns}
                onAdd={() => setCourseDialog({ open: true, record: null })}
                onEdit={(record) => setCourseDialog({ open: true, record })}
                addLabel="Add course"
                searchPlaceholder="Search courses"
                getSearchValue={(course) => `${course.name} ${course.description ?? ''}`}
                searchValue={searchQueries.courses}
                onSearchChange={(value) => updateSearchQuery('courses', value)}
              />
            </TabPanel>
            <TabPanel value="books" sx={{ p: 0, pt: 3 }}>
              <MasterSection
                title="Books"
                description="Books used for reading plans and seva assignments."
                items={sortedBooks}
                columns={bookColumns}
                onAdd={() => setBookDialog({ open: true, record: null })}
                onEdit={(record) => setBookDialog({ open: true, record })}
                addLabel="Add book"
                searchPlaceholder="Search books"
                getSearchValue={(book) => `${book.title} ${book.author ?? ''} ${book.description ?? ''}`}
                searchValue={searchQueries.books}
                onSearchChange={(value) => updateSearchQuery('books', value)}
              />
            </TabPanel>
            <TabPanel value="spiritualMasters" sx={{ p: 0, pt: 3 }}>
              <MasterSection
                title="Spiritual masters"
                description="Initiating and guiding spiritual masters available for devotees."
                items={sortedSpiritualMasters}
                columns={spiritualMasterColumns}
                onAdd={() => setSpiritualMasterDialog({ open: true, record: null })}
                onEdit={(record) => setSpiritualMasterDialog({ open: true, record })}
                addLabel="Add spiritual master"
                searchPlaceholder="Search spiritual masters"
                getSearchValue={(master) => `${master.name} ${master.centerName ?? ''}`}
                searchValue={searchQueries.spiritualMasters}
                onSearchChange={(value) => updateSearchQuery('spiritualMasters', value)}
              />
            </TabPanel>
          </Tabs>
        )}

        <Modal open={courseDialog.open} onClose={() => setCourseDialog(emptyDialogState)}>
          <ModalDialog aria-labelledby="course-dialog-title" sx={{ minWidth: 420 }}>
            <ModalClose />
            <Typography id="course-dialog-title" level="title-lg">
              {courseDialog.record ? 'Edit course' : 'Add course'}
            </Typography>
            <Typography level="body-sm" color="neutral">
              Capture information about the devotional course.
            </Typography>
            <form onSubmit={handleCourseSubmit}>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <FormControl required>
                  <FormLabel>Course name</FormLabel>
                  <Input
                    name="name"
                    defaultValue={courseDialog.record?.name ?? ''}
                    autoFocus
                    required
                    slotProps={{
                      input: {
                        minLength: COURSE_NAME_MIN_LENGTH,
                        maxLength: COURSE_NAME_MAX_LENGTH,
                      },
                    }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Order</FormLabel>
                  <Input
                    name="orderIndex"
                    type="number"
                    defaultValue={courseDialog.record?.orderIndex ?? ''}
                    slotProps={{
                      input: {
                        min: 1,
                        step: 1,
                        inputMode: 'numeric',
                      },
                    }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    name="description"
                    minRows={2}
                    defaultValue={courseDialog.record?.description ?? ''}
                    slotProps={{
                      textarea: {
                        maxLength: COURSE_DESCRIPTION_MAX_LENGTH,
                      },
                    }}
                  />
                </FormControl>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button variant="plain" color="neutral" onClick={() => setCourseDialog(emptyDialogState)}>
                    Cancel
                  </Button>
                  <Button type="submit" loading={saving}>
                    Save course
                  </Button>
                </Stack>
              </Stack>
            </form>
          </ModalDialog>
        </Modal>

        <Modal open={bookDialog.open} onClose={() => setBookDialog(emptyDialogState)}>
          <ModalDialog aria-labelledby="book-dialog-title" sx={{ minWidth: 420 }}>
            <ModalClose />
            <Typography id="book-dialog-title" level="title-lg">
              {bookDialog.record ? 'Edit book' : 'Add book'}
            </Typography>
            <Typography level="body-sm" color="neutral">
              Add scriptures or reading materials tracked across devotees.
            </Typography>
            <form onSubmit={handleBookSubmit}>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <FormControl required>
                  <FormLabel>Book title</FormLabel>
                  <Input
                    name="title"
                    defaultValue={bookDialog.record?.title ?? ''}
                    autoFocus
                    required
                    slotProps={{
                      input: {
                        minLength: BOOK_TITLE_MIN_LENGTH,
                        maxLength: BOOK_TITLE_MAX_LENGTH,
                      },
                    }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Author</FormLabel>
                  <Input
                    name="author"
                    defaultValue={bookDialog.record?.author ?? ''}
                    slotProps={{
                      input: {
                        maxLength: BOOK_AUTHOR_MAX_LENGTH,
                      },
                    }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    name="description"
                    minRows={2}
                    defaultValue={bookDialog.record?.description ?? ''}
                    slotProps={{
                      textarea: {
                        maxLength: BOOK_DESCRIPTION_MAX_LENGTH,
                      },
                    }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Display order</FormLabel>
                  <Input
                    name="orderIndex"
                    type="number"
                    defaultValue={bookDialog.record?.orderIndex ?? ''}
                    slotProps={{ input: { min: 0, step: 1, inputMode: 'numeric', pattern: '\\d*' } }}
                  />
                </FormControl>
                <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                  <FormLabel>Primary reading</FormLabel>
                  <Switch
                    defaultChecked={bookDialog.record?.isPrimary ?? false}
                    slotProps={{ input: { name: 'isPrimary' } }}
                  />
                </FormControl>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button variant="plain" color="neutral" onClick={() => setBookDialog(emptyDialogState)}>
                    Cancel
                  </Button>
                  <Button type="submit" loading={saving}>
                    Save book
                  </Button>
                </Stack>
              </Stack>
            </form>
          </ModalDialog>
        </Modal>

        <Modal
          open={spiritualMasterDialog.open}
          onClose={() => setSpiritualMasterDialog(emptyDialogState)}
        >
          <ModalDialog aria-labelledby="spiritual-master-dialog-title" sx={{ minWidth: 420 }}>
            <ModalClose />
            <Typography id="spiritual-master-dialog-title" level="title-lg">
              {spiritualMasterDialog.record ? 'Edit spiritual master' : 'Add spiritual master'}
            </Typography>
            <Typography level="body-sm" color="neutral">
              Manage the list of initiating and guiding spiritual masters.
            </Typography>
            <form onSubmit={handleSpiritualMasterSubmit}>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <FormControl required>
                  <FormLabel>Name</FormLabel>
                  <Input
                    name="name"
                    defaultValue={spiritualMasterDialog.record?.name ?? ''}
                    autoFocus
                    required
                    slotProps={{
                      input: {
                        minLength: SPIRITUAL_MASTER_NAME_MIN_LENGTH,
                        maxLength: SPIRITUAL_MASTER_NAME_MAX_LENGTH,
                      },
                    }}
                  />
                </FormControl>

                <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                  <FormLabel>Active</FormLabel>
                  <Switch
                    defaultChecked={spiritualMasterDialog.record?.isActive ?? true}
                    slotProps={{ input: { name: 'isActive' } }}
                  />
                </FormControl>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="plain"
                    color="neutral"
                    onClick={() => setSpiritualMasterDialog(emptyDialogState)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" loading={saving}>
                    Save spiritual master
                  </Button>
                </Stack>
              </Stack>
            </form>
          </ModalDialog>
        </Modal>

      </Stack>
    </Box>
  );
}
